import invokeStandaloneLLMCall from 'common/src/modules/shared/copilot/utils/invokeStandaloneLLMCall';
import {CampaignSignal} from 'common/src/modules/excelToCampaign/excelToCampaign.types';
import {z} from 'zod';
import {findMappingCandidatesResponseSchema} from '../findMappingCandidatesTool.schemas';
import {ExcelColumnInfo} from '../findMappingCandidatesTool.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';

export default async function findMappingCandidatesWithLLM(
  excelColumns: ExcelColumnInfo[],
  campaignSignals: CampaignSignal[],
  abortSignal: AbortSignal,
): Promise<z.infer<typeof findMappingCandidatesResponseSchema>> {
  const instructions = `
You are an expert at mapping Excel headers to campaign elements.
The reasons you provide are always short and concise, limited to 1 short sentence.

You need to map the excel headers to the following campaign elements:
* [Mandatory] Creative ID - Identifies the creative.
* [Optional] Creative Name - Name of the creative.
* [Optional] Creative Landing Page - Landing page of the creative.
* [Optional] Schedule - Start and end dates for creative delivery.
* [Optional] Weight - Prioritizes creatives when conditions overlap. Look for columns with values like 1-100 or 1-10. Non-numeric values and boolean values are not valid weight values.
* [Optional] Segmentation - Defines how creatives are targeted. Look for column headers semantically similar to 'Segment', 'Ad group', 'Audiences', 'Placement', or 'Custom_1', 'Custom_2', etc. These often map to signals under 'placement.*' or similar categories.

For each campaign element, you need to find the most relevant excel headers, and give them scores according to the following criteria:

### Scoring Criteria for Mapping Excel Headers to Campaign Signals:
* Name Match (0-50 points): How closely the header name matches the signal name
  * 50: Exact match or trivial variation (e.g., "Creative_ID" for "Creative ID")
  * 30: Contains the signal name (e.g., "Campaign Creative ID")
  * 10: Semantically similar (e.g., "Asset Identifier" for "Creative ID")
  
* Type Compatibility (0-30 points): How well the data types align
  * 30: Perfect type match (e.g., dates for schedule fields)
  * 15: Compatible but requires transformation
  * 0: Incompatible types
  
* Format/Pattern Match (0-20 points): How well the data format matches expectations
  * 20: Matches expected pattern perfectly (e.g., weight values between 1-100)
  * 10: Partial pattern match
  * 0: No pattern match

### Score each potential mapping according to the following campaign elements definitions:

* **Creative ID**: Identifies the creative. 
  * Related card type: Creative Card
  * Maximum expected candidates: 1

* **Creative Name**: Name of the creative.
  * Related card type: Creative Card
  * Maximum expected candidates: 1
  
* **Creative Landing Page**: Landing page of the creative.
  * Related card type: Creative Card
  * Maximum expected candidates: 1

* **Creative UTM Parameters**: UTM parameters for the creative.
  * Related card type: Creative Card
  * Maximum expected candidates: 1
  
* **Start Date**: Start date for creative delivery.
  * Related card type: Schedule Card
  * Header candidates: 
    * Header with date values
    * Header with text values of schedule options like 'ASAP', 'TBD', 'TBD (ASAP)', etc.
  * Maximum expected candidates: 1

* **End Date**: End date for creative delivery.
  * Related card type: Schedule Card
  * Header candidates: 
    * Header with date values
    * Header with text values of schedule options like 'ASAP', 'TBD', 'TBD (ASAP)', etc.
  * Maximum expected candidates: 1

* **Weight**: Prioritizes creatives when conditions overlap.
  * Related card type: Weighted Card  
  * Header candidates: 
    * Header with numeric values in ranges like 1-100 or 1-10
  * Maximum expected candidates: 1
  * If there is another header named "Audience Split", the rows with "TRUE" in the "Audience Split" column should be mapped to the a Weighted Card with audience split.

* **Segmentation**: Defines how creatives are targeted.
  * Related card type: Condition Card
  * Header candidates:
    * Headers semantically or contextually similar to 'Segment', 'Ad group', 'Audiences', 'Placement', 'Custom_1', etc.
  * Maximum expected candidates: 1
`;

  const userPrompt = `I will provide you with a list of excel headers and sample values, and a list of campaign signals.
Find potential candidates for mapping Excel headers to campaign signals.

For each candidate mapping, assign a score using the scoring criteria:
1. Calculate Name Match score (0-50 points)
2. Calculate Type Compatibility score (0-30 points)
3. Calculate Format/Pattern Match score (0-20 points)
4. Sum the scores for a total score (0-100)
5. Go over *ALL* excel headers and classify them to candidates or non-candidates.
5.1 According to the maximum expected candidates for each campaign element, choose only the top candidates according to the score. *** IMPORTANT: Never return more than the maximum expected candidates for each campaign element.***
6. Include the score, confidence level, and a brief reason for each candidate mapping.

Here are the excel headers and sample values:
  ${JSON.stringify(excelColumns)}

  Here are the campaign signals:
  ${JSON.stringify(campaignSignals)}
  
`;

  const response = await invokeStandaloneLLMCall({
    messages: [openAISystemMessage(instructions), openAIUserMessage(userPrompt)],
    responseSchema: findMappingCandidatesResponseSchema,
    abortSignal,
  });

  return response;
}
